import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useNavigate, Link } from "@tanstack/react-router";
import { useDjRestAuthRegistrationCreate } from "@/api/lctApIComponents";
import { CustomRegisterRequest } from "@/api/lctApISchemas";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export function CreateAccountForm() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const createAccountMutation = useDjRestAuthRegistrationCreate({
    onSuccess: () => {
      toast({
        variant: "default",
        title: "Success!",
        description: `You have successfully created an account for ${form.getValues().email}`,
      });
      navigate({ to: "/login", search: { email: form.getValues().email } });
    },
    onError(error: unknown) {
      let errorMessage = "Something went wrong, please try again";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorData = (error as any)?.response?.data || (error as any)?.stack;

      if (errorData && typeof errorData === "object") {
        if (errorData.password1 && Array.isArray(errorData.password1)) {
          errorMessage = errorData.password1.join("\n");
        } else if (errorData.email && Array.isArray(errorData.email)) {
          errorMessage = errorData.email.join("\n");
        } else {
          const allErrors = Object.values(errorData)
            .filter((value) => Array.isArray(value))
            .flat()
            .join("\n");

          if (allErrors) {
            errorMessage = allErrors;
          }
        }
      }

      toast({
        variant: "destructive",
        title: "Error!",
        description: <div className="whitespace-pre-line">{errorMessage}</div>,
      });
    },
  });

  const formSchema = z
    .object({
      first_name: z.string().min(1, "First name is required"),
      last_name: z.string().min(1, "Last name is required"),
      ranch_name: z.string().min(1, "Ranch name is required"),
      email: z.string().email(),
      password1: z.string().min(3, "Password is not strong enough"),
      password2: z.string().min(3, "Password is not strong enough"),
    })
    .superRefine(({ password1, password2 }, ctx) => {
      if (password1 !== password2) {
        ctx.addIssue({ code: "custom", path: ["password2"], message: "Passwords do not match" });
      }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      ranch_name: "",
      email: "",
      password1: "",
      password2: "",
    },
  });

  type ExtendedRegisterRequest = CustomRegisterRequest & {
    first_name: string;
    last_name: string;
    ranch_name: string;
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createAccountMutation.mutate({
      body: {
        first_name: values.first_name,
        last_name: values.last_name,
        ranch_name: values.ranch_name,
        email: values.email,
        password1: values.password1,
        password2: values.password2,
      } as ExtendedRegisterRequest,
    });
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-heading text-[32px] font-semibold leading-[48px] text-black">Create an account</h1>
        <p className="font-body text-[14px] font-normal leading-[24px] text-gray-800">Enter your email below to create your account</p>
      </div>

      <Form {...form}>
        <form id="create-account" onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-6">
          <div className="flex w-full flex-col gap-6">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John"
                      {...field}
                      className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 font-body text-base font-normal leading-7 text-slate-900"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Smith"
                      {...field}
                      className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 font-body text-base font-normal leading-7 text-slate-900"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ranch_name"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Ranch Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Smith Ranch"
                      {...field}
                      className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 font-body text-base font-normal leading-7 text-slate-900"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="johnsmith@gmail.com"
                      {...field}
                      type="email"
                      autoComplete="username email"
                      className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 font-body text-base font-normal leading-7 text-slate-900"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password1"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        {...field}
                        autoComplete="new-password"
                        spellCheck="false"
                        autoCapitalize="none"
                        className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 pr-10 font-body text-base font-normal leading-7 text-slate-900"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password2"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Confirm Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Password"
                        {...field}
                        autoComplete="new-password"
                        spellCheck="false"
                        autoCapitalize="none"
                        className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 pr-10 font-body text-base font-normal leading-7 text-slate-900"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                disabled={createAccountMutation.isPending}
                className="h-10 w-full rounded-[6px] bg-[#2d6532] px-4 py-2 font-body text-sm font-semibold leading-6 text-white hover:bg-[#2d6532]/90"
              >
                {createAccountMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create account
              </Button>
              {/* <p className="text-center font-body text-xs font-medium leading-5 text-slate-500">
                By creating an account, you accept our{" "}
                <a href="#" className="font-medium text-blue-600 underline">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href="#" className="font-medium text-blue-600 underline">
                  Privacy Statement
                </a>
              </p> */}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 text-center">
              <span className="font-body text-sm font-medium leading-[14px] text-slate-700">Already have an account?</span>
              <Link
                to="/login"
                className="flex items-center justify-center rounded border border-[#2d6532] bg-white px-4 py-2.5 font-body text-sm font-semibold leading-5 text-[#2d6532] hover:bg-gray-50"
              >
                Log In
              </Link>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
